import * as Yup from 'yup';

/**
 * Configuration options for checkout validation schema
 */
interface ValidationSchemaOptions {
  /**
   * Whether billing information and phone fields are required.
   * - true: Billing fields + phone are required (standard checkout, paid products)
   * - false: Billing fields + phone are optional (CheckoutV2 with free e-learning)
   *
   * When false, phone field still validates format if a value is provided,
   * but empty values are accepted.
   */
  requiresBillingInfo: boolean;
}

/**
 * Create checkout validation schema
 *
 * Factory function that creates a Yup validation schema for checkout form.
 * Takes translation function as parameter to avoid using React hooks in non-component code.
 *
 * When requiresBillingInfo is false (e.g., free e-learning in CheckoutV2),
 * billing address fields and phone number become optional since no payment
 * processing is required. Phone number still validates format if provided.
 *
 * @param t - Translation function from i18next
 * @param options - Configuration options including requiresBillingInfo flag
 * @returns Yup validation schema for checkout form
 */
const createSignupSchema = (
  t: (key: string) => string,
  options: ValidationSchemaOptions = { requiresBillingInfo: true },
): Yup.ObjectSchema<any> => {
  const { requiresBillingInfo } = options;

  return Yup.object().shape({
    business_option: Yup.string().required(
      t('validation_messages.signup.business_options'),
    ),
    email: Yup.string().required(t('validation_messages.signup.email')),
    invoice_email: requiresBillingInfo
      ? Yup.string()
        .email(t('validation_messages.signup.invoice_email'))
        .nullable()
        .required(t('validation_messages.signup.invoice_email'))
      : Yup.string().email().nullable().notRequired(),
    customer_reference: Yup.string()
      .max(30, t('validation_messages.signup.reference'))
      .nullable(),
    personal_given_name: Yup.string().required(
      t('validation_messages.signup.given_name'),
    ),
    personal_family_name: Yup.string().required(
      t('validation_messages.signup.family_name'),
    ),
    telephone: requiresBillingInfo
      ? Yup.string()
        .trim()
        .required(t('validation_messages.signup.telephone_required'))
        .min(10, t('validation_messages.signup.telephone_min'))
        .max(20, t('validation_messages.signup.telephone_max'))
        .matches(
          /^(?:(?:(?:\+|00)31|0)(?:\d-?\d{8}|\d\d-\d{7})|0\d{3}-\d{6})/,
          t('validation_messages.signup.telephone_invalid'),
        )
      : Yup.string()
        .trim()
        .notRequired()
        .test(
          'phone-format-if-provided',
          t('validation_messages.signup.telephone_invalid'),
          (value) => {
            if (!value || value.length === 0) return true;
            if (value.length < 10) return false;
            if (value.length > 20) return false;
            return /^(?:(?:(?:\+|00)31|0)(?:\d-?\d{8}|\d\d-\d{7})|0\d{3}-\d{6})/.test(
              value,
            );
          },
        ),
    billing_given_name: requiresBillingInfo
      ? Yup.string().required(t('validation_messages.signup.given_name'))
      : Yup.string().notRequired(),
    billing_family_name: requiresBillingInfo
      ? Yup.string().required(t('validation_messages.signup.family_name'))
      : Yup.string().notRequired(),
    billing_organization: requiresBillingInfo
      ? Yup.string().when('business_option', {
        is: (val: string) => val === 'business',
        then: Yup.string()
          .nullable()
          .required(t('validation_messages.signup.billing_organisation'))
          .max(60),
        otherwise: Yup.string().notRequired(),
      })
      : Yup.string().notRequired(),
    billing_locality: requiresBillingInfo
      ? Yup.string().required(t('validation_messages.signup.billing_locality'))
      : Yup.string().notRequired(),
    billing_postal_code: requiresBillingInfo
      ? Yup.string()
        .required(t('validation_messages.signup.billing_postal_code'))
        .matches(
          /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-zA-Z ._-]{2}$/,
          t('validation_messages.signup.billing_postal_code_invalid'),
        )
      : Yup.string().notRequired(),
    billing_address_line1: requiresBillingInfo
      ? Yup.string().required(
        t('validation_messages.signup.billing_address_line1'),
      )
      : Yup.string().notRequired(),
    billing_address_line2: requiresBillingInfo
      ? Yup.string()
        .trim()
        .required(t('validation_messages.signup.billing_address_line2'))
        .matches(
          /^(?:[0-9]{1})/,
          t('validation_messages.signup.housenumber.matches'),
        )
      : Yup.string().notRequired(),
    organization: Yup.string().notRequired().nullable(),
    locality: Yup.string().when('delivery_differs_from_billing', {
      is: (val: boolean) => val === true,
      then: Yup.string().required(
        t('validation_messages.signup.shipping_locality'),
      ),
      otherwise: Yup.string().notRequired(),
    }),
    postal_code: Yup.string().when('delivery_differs_from_billing', {
      is: (val: boolean) => val === true,
      then: Yup.string()
        .required(t('validation_messages.signup.billing_postal_code'))
        .matches(
          /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-zA-Z ._-]{2}$/,
          t('validation_messages.signup.billing_postal_code_invalid'),
        ),
      otherwise: Yup.string().notRequired(),
    }),
    address_line1: Yup.string().when('delivery_differs_from_billing', {
      is: (val: boolean) => val === true,
      then: Yup.string().required(
        t('validation_messages.signup.shipping_address_line1'),
      ),
      otherwise: Yup.string().notRequired(),
    }),
    address_line2: Yup.string()
      .trim()
      .when('delivery_differs_from_billing', {
        is: (val: boolean) => val === true,
        then: Yup.string()
          .required(t('validation_messages.signup.shipping_address_line2'))
          .matches(
            /^(?:[0-9]{1})/,
            t('validation_messages.signup.housenumber.matches'),
          ),
        otherwise: Yup.string().notRequired(),
      }),
    set_default: Yup.string(),
  });
};

export default createSignupSchema;
